// PitchIntensityAnimation.jsx
import { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  CategoryScale,
  Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { colormaps, defaultColormap } from "./colormaps";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController,
  Filler
);

// ColorBar component that matches the chart's color mapping
const ColorBar = ({ colormap, min = 41, max = 86 }) => {
  const steps = 100;
  const gradientStops = [];
  
  for (let i = 0; i <= steps; i++) {
    const normalized = i / steps;
    const mappedIntensity = normalized * 90; // Map to 0-90 for colormap
    const color = colormap?.getColor?.(mappedIntensity) || 'rgb(100, 100, 100)';
    gradientStops.push(`${color} ${normalized * 100}%`);
  }
  
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <span>{min}dB</span>
        <div 
          className="flex-1 h-4 rounded"
          style={{
            background: `linear-gradient(to right, ${gradientStops.join(', ')})`
          }}
        />
        <span>{max}dB</span>
      </div>
      <div className="flex justify-between text-xs text-gray-500 px-1">
        <span>50dB</span>
        <span>60dB</span>
        <span>70dB</span>
        <span>80dB</span>
      </div>
    </div>
  );
};

export default function PitchIntensityAnimation({
  intensity,
  pitch,
  segment,
  broadcastTime,
  showPitch = true,
  selectedColormap = defaultColormap || 'speechStable',  // Default prop
  onColormapChange,  // Optional prop
}) {
  const [internalColormap, setInternalColormap] = useState(selectedColormap);
  const [isMobile, setIsMobile] = useState(false);
  const [showWordLabels, setShowWordLabels] = useState(true);
  const chartRef = useRef();

  // Use the prop if provided, otherwise use internal state
  const currentColormap = onColormapChange ? selectedColormap : internalColormap;

  const handleColormapChange = (newColormap) => {
    if (onColormapChange) {
      // If parent provides callback, use it
      onColormapChange(newColormap);
    } else {
      // Otherwise use internal state
      setInternalColormap(newColormap);
    }
  };

  // Calculate wordIntervals first
  const wordIntervals = segment?.words?.map((w) => ({
    start: w.start - segment.start,
    end: w.end - segment.start,
    text: w.text,
  })) || [];

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setShowWordLabels(!mobile && wordIntervals.length <= 12);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [wordIntervals.length]);

  const pitchXs = pitch?.xs || [];
  const pitchYs = pitch?.ys || [];
  const intensityXs = intensity?.xs || [];
  const intensityYs = intensity?.ys || [];

  if (!pitchXs.length || !intensityXs.length) return null;

  // Use pitch for Y-axis scaling
  const yMin = Math.min(...pitchYs);
  const yMax = Math.max(...pitchYs);
  const yRange = yMax - yMin;

  const xMin = pitchXs[0];
  const xMax = pitchXs[pitchXs.length - 1];
  const cursorX = Math.min(Math.max(broadcastTime ?? xMin, xMin), xMax);

  // Simple smoothing function for pitch curve
  const smoothData = (data, windowSize = 3) => {
    const smoothed = [];
    for (let i = 0; i < data.length; i++) {
      let sum = 0;
      let count = 0;
      for (let j = Math.max(0, i - windowSize); j <= Math.min(data.length - 1, i + windowSize); j++) {
        sum += data[j];
        count++;
      }
      smoothed.push(sum / count);
    }
    return smoothed;
  };

  const smoothedPitchYs = smoothData(pitchYs, isMobile ? 2 : 3);

  const datasets = [];

  // 🔹 Word boundaries - more visible with darker color and thicker lines (ADD FIRST)
  wordIntervals.forEach((w) => {
    const extend = yRange * 0.1;
    datasets.push({
      label: `start-${w.text}`,
      data: [
        { x: w.start, y: yMin - extend },
        { x: w.start, y: yMax },
      ],
      borderColor: "rgba(255,0,0,0.8)", // Even more visible red
      borderWidth: isMobile ? 2 : 3, // Thicker lines
      pointRadius: 0,
      showLine: true,
      datalabels: { display: false },
    });
  });

  // ✅ Active word cursor (ADD SECOND)
  const active = wordIntervals.find(
    (w) => cursorX >= w.start && cursorX <= w.end
  );
  if (active) {
    datasets.push({
      label: "Active interval",
      type: "line",
      data: [
        { x: active.start, y: yMin - yRange * 0.1 },
        { x: active.end, y: yMin - yRange * 0.1 },
        { x: active.end, y: yMax },
        { x: active.start, y: yMax },
        { x: active.start, y: yMin - yRange * 0.1 },
      ],
      borderColor: "rgba(16,185,129,0.9)",
      backgroundColor: "rgba(16,185,129,0.4)",
      fill: true,
      pointRadius: 0,
      showLine: true,
      tension: 0,
      datalabels: { display: false },
    });
  }

  // 🔷 Main PITCH line with INTENSITY-colored fill (ADD THIRD)
  if (showPitch) {
    datasets.push({
      label: "Pitch (Hz)",
      data: pitchXs.map((x, i) => ({ x, y: smoothedPitchYs[i] })),
      borderColor: "#2563eb",
      borderWidth: isMobile ? 2 : 3,
      tension: isMobile ? 0.3 : 0.5,
      pointRadius: 0,
      showLine: true,
      fill: true,
      backgroundColor: (context) => {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) return null;
        
        const gradient = ctx.createLinearGradient(
          chartArea.left,
          0,
          chartArea.right,
          0
        );

        // Add safety check for colormap
        const colormap = colormaps[currentColormap] || colormaps[defaultColormap] || colormaps.speechStable;
        if (!colormap || !colormap.getColor) {
          console.error('Invalid colormap:', currentColormap);
          return 'rgba(100, 100, 100, 0.4)'; // Fallback color
        }

        // Fixed range: 41dB to 86dB (your main diapason)
        const fixedMin = 41;
        const fixedMax = 86;
        const fixedRange = fixedMax - fixedMin;

        const steps = 100;
        for (let i = 0; i <= steps; i++) {
          const xPercent = i / steps;
          const xValue = xMin + (xPercent * (xMax - xMin));
          
          // Find the closest INTENSITY value to this x position
          let closestIntensity = 0;
          let minDistance = Infinity;
          
          for (let j = 0; j < intensityXs.length; j++) {
            const distance = Math.abs(intensityXs[j] - xValue);
            if (distance < minDistance) {
              minDistance = distance;
              closestIntensity = intensityYs[j];
            }
          }
          
          // Clamp to fixed range and normalize
          const clampedIntensity = Math.max(fixedMin, Math.min(fixedMax, closestIntensity));
          const normalizedIntensity = (clampedIntensity - fixedMin) / fixedRange;
          
          // Map to 0-90 for colormap (since most colormaps expect 0-90 range)
          const mappedIntensity = normalizedIntensity * 90;
          
          try {
            const color = colormap.getColor(mappedIntensity);
            const transparentColor = color.replace('rgb(', 'rgba(').replace(')', ', 0.4)');
            gradient.addColorStop(xPercent, transparentColor);
          } catch (error) {
            console.error('Error getting color from colormap:', error);
            gradient.addColorStop(xPercent, 'rgba(100, 100, 100, 0.4)');
          }
        }

        return gradient;
      },
      datalabels: { display: false },
    });
  }

  // ✅ Moving broadcast cursor (ADD LAST - top layer)
  const extend = yRange * 0.1;
  datasets.push({
    label: "Cursor",
    data: [{ x: cursorX, y: yMin - extend }, { x: cursorX, y: yMax }],
    borderColor: "rgba(16,185,129,1)",
    borderWidth: isMobile ? 3 : 5, // Much thicker for visibility
    pointRadius: 0,
    showLine: true,
    spanGaps: true,
    datalabels: { display: false },
  });

  // 🏷 Word labels — WHITE text with background for better visibility (ADD VERY LAST)
  const shouldShowWordLabels = showWordLabels && wordIntervals.length > 0 && wordIntervals.length <= 12;
  
  if (shouldShowWordLabels) {
    datasets.push({
      label: "Word labels",
      type: "scatter",
      data: wordIntervals.map((w) => ({
        x: (w.start + w.end) / 2,
        y: yMin + yRange * 0.01,
        text: w.text,
      })),
      pointRadius: 0,
      datalabels: {
        display: true,
        color: "#ffffff",
        font: { 
          weight: "bold", 
          size: isMobile ? 11 : 13 
        },
        formatter: (v) => v.text,
        align: "bottom",
        anchor: "end",
        clip: false,
        yAdjust: isMobile ? 4 : 6,
        backgroundColor: "rgba(0,0,0,0.7)",
        padding: 4,
        borderRadius: 3,
      },
    });
  }

  const data = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    scales: {
      x: { 
        type: "linear", 
        grid: { display: false }, 
        ticks: { display: false },
        title: { display: !isMobile, text: "Time (seconds)" }
      },
      y: {
        min: yMin - yRange * 0.15,
        max: yMax,
        title: { 
          display: true, 
          text: "Pitch (Hz)",
          font: { size: isMobile ? 12 : 14 }
        },
        ticks: {
          font: { size: isMobile ? 10 : 12 }
        }
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: !isMobile,
        mode: 'nearest',
        intersect: false,
        callbacks: {
          afterBody: (context) => {
            const dataPoint = context[0];
            const xValue = dataPoint.parsed.x;
            
            // Find closest intensity value
            let closestIntensity = 0;
            let minDistance = Infinity;
            
            for (let i = 0; i < intensityXs.length; i++) {
              const distance = Math.abs(intensityXs[i] - xValue);
              if (distance < minDistance) {
                minDistance = distance;
                closestIntensity = intensityYs[i];
              }
            }
            
            return [`Intensity: ${closestIntensity.toFixed(1)} dB`];
          }
        }
      },
      datalabels: { 
        clip: false,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      {/* Colormap selector */}
      <div className="flex flex-col gap-2">
        <label className={`flex items-center gap-2 select-none ${isMobile ? 'text-xs' : 'text-sm'}`}>
          Color Map:
          <select
            value={currentColormap}
            onChange={(e) => handleColormapChange(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            {Object.entries(colormaps).map(([key, cm]) => (
              <option key={key} value={key}>
                {cm.name} - {cm.description}
              </option>
            ))}
          </select>
        </label>

        {/* Colormap legend - matches exactly what's shown in chart */}
        <ColorBar colormap={colormaps[currentColormap]} min={41} max={86} />
      </div>

      {/* Word labels toggle for mobile */}
      {isMobile && wordIntervals.length > 0 && (
        <label className="flex items-center gap-2 select-none text-xs">
          <input
            type="checkbox"
            checked={showWordLabels}
            onChange={() => setShowWordLabels(!showWordLabels)}
            className="scale-90"
          />
          Show word labels
        </label>
      )}

      {/* 📉 Chart wrapper with better mobile sizing */}
      <div className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
        isMobile ? 'h-48' : 'h-64'
      }`}>
        <Line ref={chartRef} data={data} options={options} />
      </div>

      {/* Mobile info text */}
      {isMobile && (
        <p className="text-xs text-gray-500 text-center">
          Pinch to zoom • Word labels: {showWordLabels ? 'ON' : 'OFF'}
        </p>
      )}
    </div>
  );
}